import { SentenceParagraph } from "~/components/ui/SentenceParagraph";
import { useAppSelector } from "~/hooks";
import { GeneratingSpinner } from "../components/GeneratingSpinner";
import { SectionErrorBoundary } from "../components/SectionErrorBoundary";
import { SectionWrapper } from "../components/SectionWrapper";
import { StaleWarning } from "../components/StaleWarning";
import type { SectionStatus } from "../hooks/usePageViewData";
import styles from "./SystemOverviewSection.module.scss";

type SystemOverviewSectionProps = {
  number: number;
  status: SectionStatus;
  detailPath: string;
  onGenerate: (documentType: string) => void;
  canRegenerate: boolean;
};

export const SystemOverviewSection = ({
  number,
  status,
  detailPath,
  onGenerate,
  canRegenerate,
}: SystemOverviewSectionProps) => {
  const { data, firstKey } = useAppSelector((s) => s.systemProposalReducer);
  const overview = data[firstKey]?.システム概要;

  const renderContent = () => {
    if (!overview) {
      return (
        <div className={styles["fallback"]}>
          コンテンツを表示できません。詳細ページで確認してください →
        </div>
      );
    }

    return (
      <div className={styles["content"]}>
        {overview.概要 && (
          <>
            <section className={styles["subsection"]}>
              <h3 className={styles["subsectionTitle"]}>概要</h3>
              <SentenceParagraph text={overview.概要} className={styles["description"]} />
            </section>
            <div className={styles["divider"]} />
          </>
        )}

        <section className={styles["subsection"]}>
          <h3 className={styles["subsectionTitle"]}>
            {overview.特徴 ? "特徴" : overview.メリット ? "メリット" : "特徴"}
          </h3>
          <ul className={styles["list"]}>
            {(overview.特徴 ?? overview.メリット ?? overview.導入するポイント ?? []).map((item) => (
              <li key={item} className={styles["listItem"]}>
                {item}
              </li>
            ))}
          </ul>
        </section>

        {(() => {
          const columns = [
            { label: "背景", items: overview.背景 ?? [] },
            { label: "目的", items: overview.目的 ?? [] },
          ].filter((c) => c.items.length > 0);
          if (columns.length === 0) return null;
          const firstColumn = columns[0];
          if (!firstColumn) return null;
          const heading = columns.length === 2 ? "背景と目的" : firstColumn.label;
          return (
            <>
              <div className={styles["divider"]} />
              <section className={styles["subsection"]}>
                <h3 className={styles["subsectionTitle"]}>{heading}</h3>
                <div className={styles["tableWrapper"]}>
                  <table className={styles["table"]}>
                    <thead>
                      <tr>
                        {columns.map((col) => (
                          <th key={col.label} className={styles["th"]}>
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {columns.map((col) => (
                          <td key={col.label} className={styles["td"]}>
                            <ul className={styles["list"]}>
                              {col.items.map((item) => (
                                <li key={item} className={styles["listItem"]}>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          );
        })()}
      </div>
    );
  };

  if (status === "generating") {
    return (
      <SectionWrapper
        number={number}
        title="システム概要"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("system-proposal")}
        disableGenerate={!canRegenerate}
        lockMessage="議事録をアップロードしてください"
      >
        <GeneratingSpinner />
      </SectionWrapper>
    );
  }

  if (status === "stale") {
    return (
      <SectionWrapper
        number={number}
        title="システム概要"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("system-proposal")}
        disableGenerate={!canRegenerate}
        lockMessage="議事録をアップロードしてください"
      >
        <SectionErrorBoundary sectionName="システム概要" detailPath={detailPath}>
          {renderContent()}
        </SectionErrorBoundary>
        <StaleWarning />
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      number={number}
      title="システム概要"
      status={status}
      detailPath={detailPath}
      onGenerate={() => onGenerate("system-proposal")}
      disableGenerate={!canRegenerate}
      lockMessage="議事録をアップロードしてください"
    >
      <SectionErrorBoundary sectionName="システム概要" detailPath={detailPath}>
        {renderContent()}
      </SectionErrorBoundary>
    </SectionWrapper>
  );
};
